
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlusCircle, FileText, Calendar, Settings } from 'lucide-react';
import React from 'react';
import { useToast } from '@/hooks/use-toast';
import { Switch } from '@/components/ui/switch';
import { useAppStore } from '@/lib/data';
import type { Bill } from '@/lib/types';
import { useActiveWorkspace } from '@/hooks/use-active-workspace';
import { getCurrentCurrencySymbol } from '@/lib/utils';
import { ProgressiveForm, StepContent } from '@/components/ui/progressive-form';
import { ScrollArea } from '@/components/ui/scroll-area';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  amount: z.coerce.number().positive('Amount must be positive'),
  dueDate: z.string().min(1, 'Date is required'),
  status: z.enum(['paid', 'unpaid']),
  isRecurring: z.boolean().default(false),
  recurringFrequency: z.enum(['daily', 'weekly', 'bi-weekly', 'monthly', 'quarterly', 'yearly']).optional(),
}).refine(data => {
    if (data.isRecurring && !data.recurringFrequency) {
        return false;
    }
    return true;
}, {
    message: "Recurring frequency is required for recurring bills.",
    path: ["recurringFrequency"],
});

const STEPS = [
  { id: 1, name: 'Basic Info', icon: FileText, description: 'Bill details' },
  { id: 2, name: 'Amount & Date', icon: Calendar, description: 'Financial info' },
  { id: 3, name: 'Settings', icon: Settings, description: 'Status & recurrence' },
];

import { useAuth } from '@/hooks/use-auth';
import { StreakService, ACTIVITY_TYPES } from '@/lib/streak-service';

// ... (other imports)

export function AddBillSheet() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { activeWorkspace } = useActiveWorkspace();
  const { addBill } = useAppStore();
  const [open, setOpen] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(1);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      amount: 0,
      dueDate: new Date().toISOString().split('T')[0],
      status: 'unpaid',
      isRecurring: false,
    },
  });

  const watchedValues = form.watch();

  const canProceed = React.useMemo(() => {
    if (currentStep === 1) return !!watchedValues.name?.trim();
    if (currentStep === 2) return !!watchedValues.amount && !!watchedValues.dueDate?.trim();
    if (currentStep === 3) return !!(watchedValues.isRecurring ? watchedValues.recurringFrequency : true);
    return false;
  }, [currentStep, watchedValues]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const newBill: Bill = {
        id: new Date().toISOString(),
        workspace: activeWorkspace,
        ...values
    };
    await addBill(newBill);

    // Record streak activity
    if (user?.id) {
      const activityType = values.status === 'paid' ? ACTIVITY_TYPES.BILL_PAID : ACTIVITY_TYPES.TRANSACTION_ADDED;
      StreakService.recordActivity(user.id, activityType);
    }

    toast({
      title: 'Bill Added',
      description: 'Your bill has been successfully saved.',
    });
    form.reset();
    setOpen(false);
    setCurrentStep(1);
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) {
        setCurrentStep(1);
        form.reset();
      }
    }}>
      <SheetTrigger asChild>
        <Button size="sm" className="gap-1">
          <PlusCircle className="h-3 w-3" />
          Add Bill
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0 overflow-y-auto">
        <SheetHeader className="px-4 sm:px-6 pt-6 pb-4 border-b border-slate-200">
          <SheetTitle>Add a New Bill</SheetTitle>
          <SheetDescription>
            Enter the details of your bill below.
          </SheetDescription>
        </SheetHeader>

        <ProgressiveForm
          steps={STEPS}
          currentStep={currentStep}
          onStepChange={setCurrentStep}
          canProceed={canProceed}
          onSubmit={() => form.handleSubmit(onSubmit)()}
          submitText="Save Bill"
        >
          <Form {...form}>
            <form className="flex-1 flex flex-col overflow-hidden">
              <ScrollArea className="flex-1 px-4 sm:px-6">
                <div className="py-6">
                  {/* Step 1: Basic Info */}
                  <StepContent step={1} currentStep={currentStep}>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Basic Information</h3>
                      <p className="text-sm text-slate-600 mb-4">Enter the basic details of your bill</p>
                    </div>
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bill Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Netflix Subscription" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </StepContent>

                  {/* Step 2: Amount & Date */}
                  <StepContent step={2} currentStep={currentStep}>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Amount & Due Date</h3>
                      <p className="text-sm text-slate-600 mb-4">Enter the financial details</p>
                    </div>
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="amount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Amount *</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder={`${getCurrentCurrencySymbol()} 15,000`} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="dueDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Due Date *</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </StepContent>

                  {/* Step 3: Settings */}
                  <StepContent step={3} currentStep={currentStep}>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Settings</h3>
                      <p className="text-sm text-slate-600 mb-4">Configure status and recurrence</p>
                    </div>
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Status</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a status" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="unpaid">Unpaid</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="isRecurring"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                              <div className="space-y-0.5">
                                  <FormLabel>Recurring Bill</FormLabel>
                              </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      {watchedValues.isRecurring && (
                          <FormField
                          control={form.control}
                          name="recurringFrequency"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Frequency</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a frequency" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="daily">Daily</SelectItem>
                                  <SelectItem value="weekly">Weekly</SelectItem>
                                  <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                                  <SelectItem value="monthly">Monthly</SelectItem>
                                  <SelectItem value="quarterly">Quarterly</SelectItem>
                                  <SelectItem value="yearly">Yearly</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </StepContent>
                </div>
              </ScrollArea>
            </form>
          </Form>
        </ProgressiveForm>
      </SheetContent>
    </Sheet>
  );
}
